import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import { api } from "../../lib/api";

const TONE = {
  confirmed: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  completed: "bg-zinc-800 text-mb-fg2",
  cancelled: "bg-zinc-800 text-mb-fg2",
  no_show: "bg-red-500/10 text-red-300 border-red-500/20",
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/20",
};

export default function PatientHistory() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/doctor/patients/${id}/history`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <div className="text-sm text-mb-fg2">Loading…</div>;
  const { patient, appointments } = data;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="doc-patient-history">
      <Link to="/doctor/patients" className="text-sm text-mb-fg2 hover:text-white inline-flex items-center gap-2">
        <ArrowLeft className="h-4 w-4" /> All patients
      </Link>
      <div className="rounded-xl border border-mb-border bg-mb-surface p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ patient</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight" data-testid="patient-name">{patient?.full_name || "—"}</h1>
        <div className="mt-1 text-sm text-mb-fg2">{patient?.email}</div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-mb-fg2">
          <Field label="DOB" value={patient?.dob || "—"} />
          <Field label="Gender" value={patient?.gender || "—"} />
          <Field label="Phone" value={patient?.phone || "—"} />
          <Field label="Allergies" value={(patient?.allergies || []).join(", ") || "—"} />
        </div>
        {patient?.medical_history && (
          <div className="mt-4 rounded-lg border border-mb-border bg-mb-surface2 p-4 text-sm text-mb-fg2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2 mb-1">Medical history</div>
            {patient.medical_history}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface" data-testid="patient-history-list">
        <div className="px-6 py-4 border-b border-mb-border text-sm font-medium">Visits with you</div>
        {appointments.length === 0 ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center">No visits yet.</div>
        ) : (
          <ul className="divide-y divide-mb-border">
            {appointments.map((a) => (
              <li key={a.id} className="px-5 py-4 grid grid-cols-1 sm:grid-cols-[180px_1fr_120px_auto] gap-3 sm:items-center"
                  data-testid={`history-row-${a.id}`}>
                <div className="text-sm tabular-nums text-mb-fg2 inline-flex items-center gap-1.5"><Calendar className="h-3 w-3 text-mb-fg2" />{new Date(a.slot_start).toLocaleString()}</div>
                <div className="text-sm text-mb-fg2 truncate">{a.notes ? a.notes : <span className="text-zinc-600 italic">No notes</span>}</div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${TONE[a.status] || ""} sm:justify-self-start`}>{a.status.replace("_", " ")}</span>
                <Link to={`/doctor/appointments/${a.id}`} className="text-sm text-teal-400 hover:text-teal-300">Open</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}
